import { LOCALSTORAGE_KEY_SUGGESTED_STORE } from '@bluheadless/ui-logic/src/constants'
import dynamicHydrate from '@bluheadless/ui-logic/src/dynamic-hydration/dynamic-hydrate'
import { useStoreLanguages } from '@bluheadless/ui-logic/src/hooks/store-languages/useStoreLanguages'
import { useSuggestedStore } from '@bluheadless/ui-logic/src/hooks/suggested-store'
import { useLocalStorage } from '@bluheadless/ui-logic/src/hooks/useLocalStorage'
import { useUserCountry } from '@bluheadless/ui-logic/src/hooks/user-country'
import { useSwitchStoreUrl } from '@bluheadless/ui-logic/src/hooks/useSwitchStoreUrl'
import { useWorldStores } from '@bluheadless/ui-logic/src/hooks/world-stores/useWorldStores'
import { useChangeCountryLanguage } from '@bluheadless/ui-logic/src/providers/change-country-language/change-country-language'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { useIntlDefault } from '@bluheadless/ui-logic/src/providers/intl'
import { onIntlError } from '@bluheadless/ui-logic/src/providers/intl/intl'
import { useStore } from '@bluheadless/ui-logic/src/providers/store'
import { getNextMonthDate } from '@bluheadless/ui-logic/src/utils/date'
import { useDetectBot } from 'node_modules/@bluheadless/ui-logic/src/hooks/useDetectBot'
import { useCallback, useEffect, useState } from 'react'
import { IntlProvider } from 'react-intl'

const SuggestedStoreDialog = dynamicHydrate(
	() => import('@bluheadless/ui/src/organisms/suggested-store/suggested-store-dialog'),
	{ on: ['idle'] }
)
const SuggestedStoreRow = dynamicHydrate(
	() => import('@bluheadless/ui/src/organisms/suggested-store/suggested-store-row'),
	{ on: ['idle'] }
)
/**
 * This is not a provider
 */
const SuggestedStore = () => {
	const [openSuggestedStore, setOpenSuggestedStore] = useState(false)

	const [href, setHref] = useState()

	const { code: storeCode } = useStore()

	const defaultTranslations = useIntlDefault()

	const [storageValue, setStorageValue] = useLocalStorage(LOCALSTORAGE_KEY_SUGGESTED_STORE, false)

	const {
		suggestedStore: { enabled: suggestedStoreEnabled, template },
		groupName: currentGroupName,
	} = useConfig()

	const { country: userCountry } = useUserCountry()

	const { code: suggestedCode, groupName: suggestedGroupName } = useSuggestedStore({
		countryCode: storeCode ? userCountry : null,
	})

	const { switchStoreUrl } = useSwitchStoreUrl()

	const setSuggestedStoreLocalStorage = useCallback(() => {
		setStorageValue(true, getNextMonthDate())
	}, [setStorageValue])

	const onCloseSuggestedStore = useCallback(
		(e) => {
			e.preventDefault()

			setSuggestedStoreLocalStorage()
			setOpenSuggestedStore(false)
		},
		[setSuggestedStoreLocalStorage, setOpenSuggestedStore]
	)
	const [, { openChangeCountryLanguageDropdown }] = useChangeCountryLanguage()

	const onContinueSuggestedStore = useCallback(() => {
		setSuggestedStoreLocalStorage()
	}, [setSuggestedStoreLocalStorage])

	const onIgnoreSuggestedStore = useCallback(() => {
		setSuggestedStoreLocalStorage()
		setOpenSuggestedStore(false)
		openChangeCountryLanguageDropdown()
	}, [setSuggestedStoreLocalStorage, setOpenSuggestedStore, openChangeCountryLanguageDropdown])

	const { isBot, loading } = useDetectBot()

	useEffect(() => {
		if (
			!loading &&
			!isBot &&
			!!storeCode &&
			suggestedStoreEnabled &&
			userCountry &&
			!storageValue &&
			suggestedGroupName &&
			(suggestedGroupName !== currentGroupName || template === 'row')
		) {
			setOpenSuggestedStore(true)
		}
	}, [
		suggestedStoreEnabled,
		userCountry,
		storageValue,
		suggestedGroupName,
		currentGroupName,
		storeCode,
		template,
		loading,
		isBot,
	])

	const SuggestedStoreComponent = template === 'row' ? SuggestedStoreRow : SuggestedStoreDialog

	useEffect(() => {
		setHref(switchStoreUrl(suggestedCode, userCountry))
	}, [switchStoreUrl, suggestedCode, userCountry])

	const { languages } = useStoreLanguages({ store: storeCode })
	const { isSingleWebsite } = useWorldStores()
	const isSingleLanguage = languages?.length === 1

	return (
		<>
			{!!storeCode && (
				<IntlProvider messages={defaultTranslations} locale="en-US" defaultLocale="en-US" onError={onIntlError}>
					{(!isSingleWebsite || !isSingleLanguage) && (
						<SuggestedStoreComponent
							open={openSuggestedStore}
							forceHydration={openSuggestedStore}
							currentCountryCode={userCountry}
							suggestedGroupName={suggestedGroupName}
							currentGroupName={currentGroupName}
							onClose={onCloseSuggestedStore}
							onContinue={onContinueSuggestedStore}
							onIgnore={onIgnoreSuggestedStore}
							href={href}
						/>
					)}
				</IntlProvider>
			)}
		</>
	)
}

export default SuggestedStore
